import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plane,
  FileText,
  DollarSign,
  Users,
  TrendingUp,
  Globe,
  CreditCard,
  MapPin,
  Calendar,
  Shield,
  ArrowRight,
  Sparkles,
  LogOut,
  LayoutDashboard,
} from "lucide-react";
import { BRAND_LOGO_PATH } from "@/utils/constants";

export default function Home() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const services = [
    {
      icon: <Plane className="w-8 h-8" />,
      title: "Inbound Tourism",
      description:
        "Manage incoming tourists, tour packages, hotel bookings, and local experiences with comprehensive tracking.",
      image:
        "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=400&fit=crop",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Outbound Tourism",
      description:
        "Organize international tours, travel packages, and destination management for your clients.",
      image:
        "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&h=400&fit=crop",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Visa Processing",
      description:
        "Streamline visa applications, track processing status, and manage documentation for multiple countries.",
      image:
        "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=400&fit=crop",
      color: "from-emerald-500 to-teal-500",
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Ticketing Services",
      description:
        "Handle flight bookings, hotel reservations, and travel tickets with automated invoice generation.",
      image:
        "https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=600&h=400&fit=crop",
      color: "from-orange-500 to-red-500",
    },
  ];

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Smart Invoice Management",
      description:
        "Create, approve, and manage invoices with multiple service items. Support for packages, visas, tickets, and custom services.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Real-Time Payment Tracking",
      description:
        "Track payments, manage collections, and monitor payment status in real-time with automated updates.",
      gradient: "from-emerald-500 to-teal-500",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Database",
      description:
        "Maintain comprehensive customer database with contact information, transaction history, and service preferences.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Advanced Financial Reports",
      description:
        "Generate profit & loss statements, income reports, expense reports, and collection status with PDF/Excel export.",
      gradient: "from-orange-500 to-red-500",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Currency Support",
      description:
        "Support for Omani Rial (OMR) and USD with automatic currency conversion for international clients.",
      gradient: "from-cyan-500 to-blue-500",
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Flexible VAT Management",
      description:
        "Automatic 5% VAT calculation with option to remove for international clients. Finance team has full control.",
      gradient: "from-teal-500 to-emerald-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-cyan-50">
      {/* Header — white bar + card shadow (brand) */}
      <header className="sticky top-0 z-50 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center group min-w-0">
              <img
                src={BRAND_LOGO_PATH}
                alt="SafarCore"
                className="h-14 sm:h-16 w-auto object-contain min-h-[56px] shrink-0"
              />
            </div>
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-600 capitalize">
                      {user?.role}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-2 bg-gradient-to-r from-accent to-secondary text-white rounded-xl hover:opacity-95 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="px-6 py-2.5 bg-gradient-to-r from-accent to-secondary text-white rounded-xl hover:opacity-95 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2 group"
              >
                Sign In
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=1080&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0077B6]/92 via-[#00B4D8]/75 to-[#0077B6]/90"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center text-white animate-fadeIn">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-lg px-4 py-2 rounded-full mb-6 border border-white/30">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <span className="text-sm font-semibold">
                Complete Travel Business Solution
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slideUp">
              Manage Your Travel Business
              <br />
              <span className="bg-gradient-to-r from-[#48CAE4] to-white bg-clip-text text-transparent">
                Like Never Before
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-slideUp delay-200">
              Streamline invoices, track payments, manage customers, and
              generate financial reports.
              <br />
              Everything you need for inbound, outbound, visa processing, and
              ticketing services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slideUp delay-300">
              <button
                onClick={() =>
                  document
                    .getElementById("packages")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="px-8 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-xl hover:opacity-95 transition-all duration-300 shadow-2xl font-semibold text-lg flex items-center gap-2 group"
              >
                View Plans
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("services")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="px-8 py-4 bg-white/10 backdrop-blur-lg text-white rounded-xl hover:bg-white/20 transition-all duration-300 border-2 border-white/30 font-semibold text-lg"
              >
                Our Services
              </button>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#48CAE4]/25 rounded-full blur-xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-[#00B4D8]/20 rounded-full blur-xl animate-float delay-1000"></div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
      >
        <div className="text-center mb-16 animate-fadeIn">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Travel Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive solutions for all your travel business needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative overflow-hidden bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 animate-slideUp"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                style={{ backgroundImage: `url(${service.image})` }}
              ></div>

              {/* Gradient Overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-10 group-hover:opacity-20 transition-opacity`}
              ></div>

              <div className="relative z-10 p-8">
                <div
                  className={`inline-flex p-4 bg-gradient-to-br ${service.color} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <div className="text-white">{service.icon}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeIn">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to run your travel business efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 border-transparent hover:border-primary/30 animate-slideUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div
                  className={`inline-flex p-4 bg-gradient-to-br ${feature.gradient} rounded-xl mb-6 shadow-md group-hover:scale-110 transition-transform duration-300`}
                >
                  <div className="text-white">{feature.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section
        id="packages"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
      >
        <div className="text-center mb-16 animate-fadeIn">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the perfect package for your travel business needs
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => {
              let featuresList = [];
              if (pkg.features != null) {
                if (Array.isArray(pkg.features)) {
                  featuresList = pkg.features;
                } else if (typeof pkg.features === "string") {
                  try {
                    const parsed = JSON.parse(pkg.features);
                    featuresList = Array.isArray(parsed) ? parsed : [pkg.features];
                  } catch {
                    featuresList = [pkg.features];
                  }
                }
              }
              const isPopular = pkg.name
                ?.toLowerCase()
                .includes("professional");

              return (
                <div
                  key={pkg.id}
                  className={`group relative overflow-hidden bg-white rounded-3xl shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:scale-105 animate-slideUp ${
                    isPopular
                      ? "ring-4 ring-primary scale-105 md:scale-110"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Background Pattern */}
      {/*   <div
                    className="absolute inset-0 opacity-5 bg-cover bg-center"
                    style={{
                      backgroundImage:
                        "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop')",
                    }}
                  ></div>

                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-accent via-primary to-secondary text-white text-center py-3 rounded-t-3xl shadow-lg">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
                        <span className="font-bold text-lg">Most Popular</span>
                        <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
                      </div>
                    </div>
                  )}

                  <div
                    className={`relative z-10 p-8 ${isPopular ? "pt-16" : ""}`}
                  >
                    <div className="text-center mb-6">
                      <h3 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">
                        {pkg.name}
                      </h3>
                      <p className="text-gray-600 min-h-[3rem]">
                        {pkg.description}
                      </p>
                    </div>

                    <div className="text-center mb-8 p-6 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl border-2 border-primary/25">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-4xl sm:text-5xl font-bold text-secondary">
                          Coming Soon
                        </span>
                        <span className="text-gray-600 text-lg">
                          Pricing update in progress
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-4 mb-8">
                      {featuresList.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 group/item"
                        >
                          <div className="mt-1 flex-shrink-0">
                            <div className="bg-gradient-to-br from-primary to-secondary p-1.5 rounded-lg group-hover/item:scale-110 transition-transform">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <span className="text-gray-700 leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled
                      className="w-full py-4 rounded-xl font-bold text-lg bg-gray-200 text-gray-500 cursor-not-allowed shadow-sm"
                    >
                      Coming Soon
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section> */}

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-secondary via-accent to-[#0077B6] py-20">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=600&fit=crop')",
          }}
        ></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Travel Business?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join hundreds of travel companies already using our platform to
            streamline their operations
          </p>
          <button
            onClick={() =>
              document
                .getElementById("packages")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="px-8 py-4 bg-white text-secondary rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl font-bold text-lg flex items-center gap-2 mx-auto group"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={BRAND_LOGO_PATH}
                  alt="SafarCore"
                  className="h-16 sm:h-20 w-auto object-contain bg-white rounded-lg p-2"
                />
              </div>
              <p className="text-gray-400 text-sm">
                Travel operations, invoicing, and payments for modern travel
                companies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Inbound Tourism</li>
                <li>Outbound Tourism</li>
                <li>Visa Processing</li>
                <li>Ticketing</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Features</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Invoice Management</li>
                <li>Payment Tracking</li>
                <li>Financial Reports</li>
                <li>Customer Management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>About Us</li>
                <li>Contact</li>
                <li>Privacy Policy</li>
                <li>Terms of Service</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>
              &copy; {new Date().getFullYear()} SafarCore. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
